import { useState, useCallback, useEffect } from 'react';
import http, { apiErrorMessage } from '../../Api/http';
import * as Api from '../../Api/Api';
import { parseApiDate } from '../../utils/format';
import { unpackShoot } from './payTiming';

const pad = (n) => String(n).padStart(2, '0');
const toISO = (v) => {
    const d = parseApiDate(v);
    return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : '';
};
// The API wants dd-MM-yyyy; the form's date input holds yyyy-MM-dd.
export const toDMY = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('-') : '');
const num = (v) => Number(v) || 0;

const errText = (err, fallback) => (err?.response || err?.request || err?.code ? apiErrorMessage(err, fallback) : (err?.message || fallback));

const catName = (c) => (typeof c === 'string' ? c : (c?.name ?? c?.category ?? ''));
const catId = (c) => (c && typeof c === 'object' ? (c.id ?? null) : null);

const normEmployee = (e) => ({
    code: e.employeeCode ?? e.code ?? '',
    name: e.employeeName ?? e.name ?? '',
    amount: num(e.amount), // this person's own share of the shoot expense
    prepaidTotal: num(e.prepaidTotal),
    postpaidTotal: num(e.postpaidTotal),
    netBalance: num(e.netBalance),
});

// One shape for all three tabs. Daily/Other send `price` + `description`, Shoot sends `amount` + `notes`.
const normalizeEntry = (e) => {
    const emps = Array.isArray(e.employees) ? e.employees.map(normEmployee) : [];
    return {
        id: e.id,
        date: toISO(e.date),
        category: e.category ?? '',
        amount: num(e.amount ?? e.price), // on a shoot expense this is the server's total (sum of each person's own amount)
        paymentMode: e.paymentMode ?? '',
        notes: e.notes ?? e.description ?? '',
        status: e.status ?? '',
        vendor: e.vendor ?? '',
        shootType: e.shootType ?? '',
        project: e.project ?? '',
        location: e.location ?? '',
        employees: emps.map((x) => x.name || x.code),
        employeeCodes: emps.map((x) => x.code),
        empStats: emps,
    };
};

// The shoot a payment is for comes back as shootName; older rows carried it inside the notes.
const normalizePayment = (p) => ({
    ...(p.shootName ? { shoot: p.shootName, notes: p.notes ?? '' } : unpackShoot(p.notes)),
    id: p.id,
    employeeCode: p.employeeCode ?? '',
    employeeName: p.employeeName ?? p.name ?? '',
    date: toISO(p.date),
    amount: num(p.amount),
    type: p.type ?? '',
});

const normReminder = (r) => ({
    id: r.id,
    category: r.category ?? '',
    dueDate: toISO(r.dueDate),
    remindBeforeDays: num(r.remindBeforeDays),
    remindFromDate: toISO(r.remindFromDate),
    daysRemaining: r.daysRemaining == null ? null : num(r.daysRemaining),
    status: r.status ?? '',
    isDue: Boolean(r.isDue),
    description: r.description ?? '',
    createdOn: toISO(r.createdOn),
});

const pickList = (d, ...keys) => {
    if (Array.isArray(d)) return d;
    for (const k of keys) if (Array.isArray(d?.[k])) return d[k];
    return [];
};

const payloadFor = (kind, f) => {
    const date = toDMY(f.date);
    if (kind === 'Shoot') {
        // Each employee carries their own amount; there is no top-level amount (the server adds them up).
        const employees = Array.isArray(f.employees)
            ? f.employees.map((x) => ({ employeeCode: x.employeeCode, amount: num(x.amount) }))
            : (f.employeeCodes || []).map((code) => ({ employeeCode: code, amount: num(f.empAmounts?.[code]) }));
        return {
            date, shootType: f.shootType, category: f.category, project: f.project || '', location: f.location || '',
            employees, paymentMode: f.paymentMode, notes: f.notes || '',
        };
    }
    const base = { date, category: f.category, price: num(f.amount), paymentMode: f.paymentMode, status: f.status || 'Unpaid', description: f.notes || '' };
    return kind === 'Other' ? { ...base, vendor: f.vendor || '' } : base;
};

const call = async (fn) => {
    try {
        const { data: body } = await fn();
        if (body?.error) throw new Error(body.message || 'Request failed.');
        return body;
    } catch (err) {
        throw new Error(errText(err, 'Request failed.'));
    }
};

// A missing list comes back as 404 on this API; treat it as "nothing yet".
const getOrEmpty = async (url) => {
    try {
        const { data: body } = await http.get(url);
        if (body?.error) throw new Error(body.message || 'Request failed.');
        return body?.data ?? {};
    } catch (err) {
        if (err?.response?.status === 404) return {};
        throw err;
    }
};

/**
 * An Expenses tab is unmounted when you switch away from it, so its data lives in the page that
 * owns the tabs. `config.noun` (Daily | Shoot | Other) picks which API set it talks to.
 */
export default function useExpenseData(config) {
    const kind = config.noun;
    const [rows, setRows] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryIds, setCategoryIds] = useState({});
    const [payments, setPayments] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Daily only: every saved reminder, and the ones whose remind window has started (shown on open).
    const [reminders, setReminders] = useState([]);
    const [activeReminders, setActiveReminders] = useState([]);

    const load = useCallback(async () => {
        try {
            const d = await getOrEmpty(Api[`Get${kind}Expenses`]);
            const list = Array.isArray(d.entries) ? d.entries.map(normalizeEntry) : [];
            setRows(list);
            const cats = Array.isArray(d.categories) ? d.categories : [];
            setCategories(cats.map(catName).filter(Boolean));
            setCategoryIds(Object.fromEntries(cats.map((c) => [catName(c), catId(c)]).filter(([n]) => n)));
            setSummary({ total: d.total, totalEntries: d.totalEntries, thisMonth: d.thisMonth, today: d.today, shootTypeCounts: d.shootTypeCounts, remindersSet: d.remindersSet });
            if (kind === 'Daily') {
                setActiveReminders(pickList(d.activeReminders).map(normReminder));
                const rd = await getOrEmpty(Api.GetDailyExpenseReminders);
                setReminders(pickList(rd, 'reminders', 'items').map(normReminder));
            }
            if (kind === 'Shoot') {
                const pd = await getOrEmpty(Api.GetShootPayments);
                const pl = Array.isArray(pd) ? pd : (pd.payments ?? pd.items ?? []);
                setPayments(pl.map(normalizePayment));
            }
            setError('');
        } catch (err) {
            setError(errText(err, `Could not load ${kind.toLowerCase()} expenses.`));
        } finally {
            setLoading(false);
        }
    }, [kind]);

    useEffect(() => { load(); }, [load]);

    const addExpense = async (form) => { await call(() => http.post(Api[`Add${kind}Expense`], payloadFor(kind, form))); await load(); };
    const updateExpense = async (id, form) => { await call(() => http.put(Api[`Update${kind}Expense`], { id, ...payloadFor(kind, form) })); await load(); };
    const removeExpense = async (id) => { await call(() => http.delete(Api[`Delete${kind}Expense`], { params: { id } })); await load(); };
    const renameCategory = async (id, name) => { await call(() => http.put(Api[`Update${kind}ExpenseCategory`], { id, name })); await load(); };
    const addPayment = async (p) => {
        await call(() => http.post(Api.AddShootPayment, { employeeCode: p.employeeCode, date: toDMY(p.date), amount: num(p.amount), type: p.type, shootName: (p.shoot || '').trim(), notes: p.notes || '' }));
        await load();
    };
    // Project names already logged for this employee: the Postpaid dropdown on Add Payment.
    const loadShootNames = async (employeeCode) => {
        const d = await getOrEmpty(`${Api.GetEmployeeShootNames}?employeeCode=${encodeURIComponent(employeeCode)}`);
        return pickList(d, 'shootNames', 'names', 'items', 'projects').map((x) => (typeof x === 'string' ? x : (x?.name ?? x?.project ?? x?.shootName ?? ''))).filter(Boolean);
    };
    const addReminder = async (r) => {
        await call(() => http.post(Api.SetDailyExpenseReminder, { category: r.category, dueInDays: num(r.dueInDays), remindBeforeDays: num(r.remindBeforeDays), description: r.description || '' }));
        await load();
    };
    const removeReminder = async (id) => { await call(() => http.delete(Api.DeleteDailyExpenseReminder, { params: { id } })); await load(); };
    const removePayment = async (id) => { await call(() => http.delete(Api.DeleteShootPayment, { params: { id } })); await load(); };

    return {
        rows, categories, setCategories, categoryIds, payments, summary, loading, error, reload: load,
        reminders, activeReminders, addReminder, removeReminder,
        addExpense, updateExpense, removeExpense, renameCategory, addPayment, removePayment, loadShootNames,
    };
}
